import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import {
  getServiceItemTemplates,
  createServiceItemTemplate,
  updateServiceItemTemplate,
  reorderServiceItemTemplates,
  deleteServiceItemTemplate
} from "../../services/servicesApiV2";

/**
 * WorkflowStepTemplateModal
 *
 * Manages workflow step templates (type='work') on a master service.
 * Simpler list: Label | Reorder | Delete (no product fields).
 * Steps are defined in Phase 1 but enforcement is Phase 2.
 */
const WorkflowStepTemplateModal = ({
  show,
  onHide,
  masterServiceId,
  onTemplatesChanged
}) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [originalSteps, setOriginalSteps] = useState([]);

  useEffect(() => {
    if (show && masterServiceId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, masterServiceId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const templates = await getServiceItemTemplates(masterServiceId);
      // Filter to work type. If server hasn't added the type column yet,
      // items created with type='work' may come back without the field —
      // treat items with no product fields and no explicit 'material' type as work steps.
      const workTemplates = (templates || []).filter(t =>
        t.type === 'work'
      );

      const rows = workTemplates.map(t => ({
        _key: t.id,
        id: t.id,
        label: t.label || '',
        sort_order: t.sort_order || 0
      }));

      setSteps(rows);
      setOriginalSteps(JSON.parse(JSON.stringify(rows)));
    } catch (err) {
      console.error('Error loading workflow steps:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    setSteps(prev => [...prev, {
      _key: `new-${Date.now()}`,
      id: null,
      label: '',
      sort_order: prev.length
    }]);
  };

  const removeRow = (index) => {
    setSteps(prev => prev.filter((_, i) => i !== index));
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const updateLabel = (index, value) => {
    setSteps(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], label: value };
      return updated;
    });
    if (validationErrors[index]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const moveRow = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    setSteps(prev => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  };

  const validate = () => {
    const errors = {};
    const labels = {};

    steps.forEach((step, index) => {
      if (!step.label.trim()) {
        errors[index] = 'Label is required';
        return;
      }
      const lower = step.label.trim().toLowerCase();
      if (labels[lower] !== undefined) {
        errors[index] = 'Duplicate label';
        errors[labels[lower]] = 'Duplicate label';
      }
      labels[lower] = index;
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const originalMap = {};
      originalSteps.forEach(o => { if (o.id) originalMap[o.id] = o; });

      const currentIds = new Set(steps.filter(s => s.id).map(s => s.id));

      // Delete removed steps
      for (const orig of originalSteps) {
        if (orig.id && !currentIds.has(orig.id)) {
          await deleteServiceItemTemplate(orig.id);
        }
      }

      // Create new steps, update changed steps
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (!step.id) {
          await createServiceItemTemplate(masterServiceId, {
            label: step.label.trim(),
            type: 'work',
            sort_order: i
          });
        } else {
          const orig = originalMap[step.id];
          if (orig && orig.label !== step.label.trim()) {
            await updateServiceItemTemplate(step.id, {
              label: step.label.trim()
            });
          }
        }
      }

      // Reorder
      const orderedIds = steps.filter(s => s.id).map(s => s.id);
      if (orderedIds.length > 0) {
        const hasNew = steps.some(s => !s.id);
        if (!hasNew) {
          await reorderServiceItemTemplates(masterServiceId, orderedIds);
        }
      }

      if (onTemplatesChanged) onTemplatesChanged();
      onHide();
    } catch (err) {
      console.error('Error saving workflow steps:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-sharp fa-regular fa-tasks mr-2"></i>
          Workflow Steps
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading...
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <>
            <p className="text-muted small mb-3">
              Define workflow steps for this service. These are operational steps
              that technicians will complete when working on the service.
            </p>

            {steps.length === 0 ? (
              <div className="text-center text-muted py-3">
                No workflow steps defined yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: '8%' }}>#</th>
                      <th style={{ width: '72%' }}>Step Description</th>
                      <th style={{ width: '12%' }}>Order</th>
                      <th style={{ width: '8%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.map((step, index) => (
                      <tr key={step._key}>
                        <td className="text-muted align-middle">{index + 1}.</td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={step.label}
                            onChange={(e) => updateLabel(index, e.target.value)}
                            placeholder="Step description"
                            isInvalid={!!validationErrors[index]}
                          />
                          {validationErrors[index] && (
                            <Form.Text className="text-danger">{validationErrors[index]}</Form.Text>
                          )}
                        </td>
                        <td className="align-middle">
                          <div className="d-flex">
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 mr-1"
                              onClick={() => moveRow(index, -1)}
                              disabled={index === 0}
                              title="Move up"
                            >
                              <i className="fa-sharp fa-regular fa-chevron-up"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0"
                              onClick={() => moveRow(index, 1)}
                              disabled={index === steps.length - 1}
                              title="Move down"
                            >
                              <i className="fa-sharp fa-regular fa-chevron-down"></i>
                            </button>
                          </div>
                        </td>
                        <td className="align-middle">
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => removeRow(index)}
                            title="Remove"
                          >
                            <i className="fa-sharp fa-regular fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              className="btn btn-sm btn-outline-primary mt-1"
              onClick={addRow}
            >
              <i className="fa-sharp fa-regular fa-plus mr-1"></i> Add Step
            </button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="mr-1" />
              Saving...
            </>
          ) : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WorkflowStepTemplateModal;
